import { Dispatch } from '@reduxjs/toolkit';
import { getLayerNameByElemType } from 'common/helpers';
import { parseInt } from 'common/helpers';
import { ElemTypes, IElement } from 'common/interfaces';
import {
  IDroppedItem,
  LayerTypes,
} from 'modules/LayersSidebar/LayersSidebar.interfaces';
import { IGroupAgainItem, updateElems } from 'store/reducers/elems.slice';

import CollectionService from './CollectionService';
import ElemsService from './ElemsService';

class LayersService {
  transformElemsToLayers = (elems: IElement[]): IElement[] => {
    if (!elems.length) return [];

    let layers: IElement[] = [];

    for (const elem of elems) {
      if (ElemsService.checkGroupElem(elem) && elem.children) {
        const childrenLayers: IElement[] = [];

        let groupLayers = [
          {
            ...elem,
            children: null,
            layerName: elem.layerName + ' ' + elem.id.slice(0, 5),
          } as IElement,
        ];

        if (elem.showNestedLayers) {
          for (const child of elem.children) {
            childrenLayers.push({
              ...child,
              layerName: child.layerName + ' ' + child.id.slice(0, 5),
            });
          }
        } else {
          // если дети должны быть скрыты запоминаем их в поле children
          groupLayers[0].children = elem.children;
        }

        groupLayers = [...childrenLayers, ...groupLayers];

        layers = [...layers, ...groupLayers];
      } else {
        layers.push({
          ...elem,
          layerName: elem.layerName + ' ' + elem.id.slice(0, 5),
        });
      }
    }

    return layers.reverse();
  };

  _transformLayersToElems = (layers: IElement[]): IElement[] => {
    let updatedElems: IElement[] = [];
    const handledGroupIds: string[] = [];

    for (const layer of layers) {
      if (!layer.groupId) {
        updatedElems.push({
          ...layer,
          layerName: getLayerNameByElemType(layer.elemType),
        });
      }
    }

    for (const layer of layers) {
      if (layer.groupId && !handledGroupIds.includes(layer.groupId)) {
        updatedElems = updatedElems.map((elem) => {
          if (elem.id === layer.groupId) {
            const elemChildren = layers
              .filter((item) => item.groupId === layer.groupId)
              .map((item) => ({
                ...item,
                layerName: getLayerNameByElemType(item.elemType),
              }));

            const updatedElem: IElement = {
              ...elem,
              children: elemChildren.reverse(),
            };

            return updatedElem;
          }

          return elem;
        });

        handledGroupIds.push(layer.groupId);
      }
    }

    return updatedElems.reverse();
  };

  _findLayerSiblings = (
    sourceIndex: number,
    destinationIndex: number,
    layers: IElement[],
  ): {
    prevLayer: IElement | null;
    nextLayer: IElement | null;
  } => {
    let prevLayer: IElement | null = null;
    let nextLayer: IElement | null = null;

    if (destinationIndex < sourceIndex) {
      prevLayer = destinationIndex === 0 ? null : layers[destinationIndex - 1];

      nextLayer =
        destinationIndex === layers.length - 1
          ? null
          : layers[destinationIndex];
    } else {
      prevLayer = destinationIndex === 0 ? null : layers[destinationIndex];

      nextLayer =
        destinationIndex === layers.length - 1
          ? null
          : layers[destinationIndex + 1];
    }

    return { prevLayer, nextLayer };
  };

  _removeLayersIfNeeded = (
    layersToRemove: IElement[],
    layersList: IElement[],
  ): IElement[] => {
    if (layersToRemove.length) {
      let newLayersList: IElement[] = [];

      layersToRemove.forEach((layerItem) => {
        const leftValue = parseInt(String(layerItem.styles.left));
        const topValue = parseInt(String(layerItem.styles.top));

        newLayersList = layersList
          .filter((item) => item.id !== layerItem.id)
          .map((item) => {
            if (item.groupId === layerItem.id) {
              return {
                ...item,
                groupId: null,
                styles: {
                  ...item.styles,
                  left: leftValue + parseInt(String(item.styles.left)) + 'px',
                  top: topValue + parseInt(String(item.styles.top)) + 'px',
                },
              };
            }
            return item;
          });
      });

      return newLayersList;
    } else {
      return layersList;
    }
  };

  _getGroupElemsChildren(
    layersList: IElement[],
    sourceGroupId: string | null,
    tragetGroupId: string | undefined,
  ): {
    sourceGroupChildren: IElement[];
    targetGroupChildren: IElement[];
  } {
    let sourceGroupChildren: IElement[] = [];
    let targetGroupChildren: IElement[] = [];

    for (const layer of layersList) {
      if (
        layer.id === sourceGroupId &&
        layer.showNestedLayers === false &&
        layer.children
      ) {
        sourceGroupChildren = [...sourceGroupChildren, ...layer.children];
      }

      if (sourceGroupId && layer.groupId === sourceGroupId) {
        sourceGroupChildren.push(layer);
      }

      if (
        layer.id === tragetGroupId &&
        layer.showNestedLayers === false &&
        layer.children
      ) {
        targetGroupChildren = [...targetGroupChildren, ...layer.children];
      }

      if (layer.groupId === tragetGroupId) {
        targetGroupChildren.push(layer);
      }
    }

    return { sourceGroupChildren, targetGroupChildren };
  }

  _updateSourceGroupData(
    layerId: string | undefined,
    sourceGroupElem: IElement,
    initialLayers: IElement[],
    updatedLayersList: IElement[],
  ): {
    isNeedToDeleteSourceGroup: boolean;
    updatedLayersList: IElement[] | null;
  } {
    const isNeedToDeleteSourceGroup =
      initialLayers.filter((item) => item.groupId === sourceGroupElem.id)
        .length === 2;

    if (isNeedToDeleteSourceGroup) {
      return { isNeedToDeleteSourceGroup: true, updatedLayersList: null };
    } else {
      const sourceGroupChildren = updatedLayersList.filter(
        (item) => item.groupId === sourceGroupElem.id && item.id !== layerId,
      );

      const ungroupedSourceChildren = ElemsService.ungroupGroupChildren(
        sourceGroupElem,
        sourceGroupChildren,
      );

      const newUpdatedLayersList = ElemsService.replaceGroupChildren(
        updatedLayersList,
        sourceGroupElem.id,
        ungroupedSourceChildren,
      );

      return {
        isNeedToDeleteSourceGroup: false,
        updatedLayersList: newUpdatedLayersList,
      };
    }
  }

  getLayerType(layer: IElement): LayerTypes {
    if (layer.elemType !== ElemTypes.GROUP && !layer.groupId) {
      return LayerTypes.SIMPLE; // обычный слой
    } else if (layer.elemType === ElemTypes.GROUP) {
      return LayerTypes.GROUP; // слой группа
    } else if (layer.groupId) {
      return LayerTypes.CHILD; // дочерний слой группы
    } else {
      return LayerTypes.SIMPLE;
    }
  }

  changeLayerPosition = (
    droppedItem: IDroppedItem,
    initialLayers: IElement[],
    dispatch: Dispatch,
  ): void => {
    if (!droppedItem.destination) return;

    const sourceIndex = droppedItem.source.index;
    const destinationIndex = droppedItem.destination.index;

    let updatedLayersList = CollectionService.cloneDeep(initialLayers);

    const layersToRemove: IElement[] = [];

    let layer = updatedLayersList.find(
      (item) => item.id === droppedItem.draggableId,
    );

    if (!layer) return;

    const { prevLayer, nextLayer } = this._findLayerSiblings(
      sourceIndex,
      destinationIndex,
      updatedLayersList,
    );

    const layerType = this.getLayerType(layer);

    // дочерний слой
    if (layerType === LayerTypes.CHILD) {
      const sourceGroupId = layer.groupId;

      // извлекаем из группы (и удаляем группу, если в ней остался только один элемент)
      if (
        !prevLayer ||
        (prevLayer &&
          prevLayer.elemType !== ElemTypes.GROUP &&
          !prevLayer.groupId)
      ) {
        const sourceGroupElem = updatedLayersList.find(
          (item) => item.id === sourceGroupId,
        );

        if (!sourceGroupElem) return;

        layer = ElemsService.ungroupGroupChildren(
          sourceGroupElem,
          [layer],
          true,
        )?.[0];

        const result = this._updateSourceGroupData(
          layer?.id,
          sourceGroupElem,
          initialLayers,
          updatedLayersList,
        );

        if (result.isNeedToDeleteSourceGroup) {
          layersToRemove.push(sourceGroupElem);
        } else if (result.updatedLayersList) {
          updatedLayersList = result.updatedLayersList;
        }
      }

      // помещаем в другую группу
      if (
        prevLayer &&
        nextLayer?.groupId &&
        (prevLayer.elemType === ElemTypes.GROUP || prevLayer.groupId) &&
        nextLayer.groupId !== layer.groupId
      ) {
        const sourceGroupElem = updatedLayersList.find(
          (item) => item.id === sourceGroupId,
        );

        const targetGroupElem = updatedLayersList.find(
          (item) => item.id === nextLayer?.groupId,
        );

        if (!targetGroupElem || !sourceGroupElem) return;

        const { targetGroupChildren } = this._getGroupElemsChildren(
          updatedLayersList,
          sourceGroupElem.id,
          targetGroupElem.id,
        );

        const ungroupedElem = ElemsService.ungroupGroupChildren(
          sourceGroupElem,
          [layer],
        )?.[0];

        const ungroupedTargetChildren = ElemsService.ungroupGroupChildren(
          targetGroupElem,
          targetGroupChildren,
        );

        const updatedTargetChildren = [
          ungroupedElem,
          ...ungroupedTargetChildren,
        ];

        updatedLayersList = ElemsService.replaceGroupChildren(
          updatedLayersList,
          targetGroupElem.id,
          updatedTargetChildren,
        );

        const updatedLayer = updatedLayersList.find(
          (item) => item.id === layer?.id,
        );

        if (!updatedLayer) return;

        layer = updatedLayer;

        const result = this._updateSourceGroupData(
          layer?.id,
          sourceGroupElem,
          initialLayers,
          updatedLayersList,
        );

        if (result.isNeedToDeleteSourceGroup) {
          layersToRemove.push(sourceGroupElem);
        } else if (result.updatedLayersList) {
          updatedLayersList = result.updatedLayersList;
        }
      }
    }

    // простой слой
    if (layerType === LayerTypes.SIMPLE) {
      // помещаем внутрь группы
      if (
        prevLayer &&
        (prevLayer.groupId || prevLayer.elemType === ElemTypes.GROUP) &&
        nextLayer?.groupId
      ) {
        const targetGroupElem = initialLayers.find(
          (item) => item.id === nextLayer?.groupId,
        );

        if (!targetGroupElem) return;

        const { targetGroupChildren } = this._getGroupElemsChildren(
          updatedLayersList,
          null,
          targetGroupElem.id,
        );

        const ungroupedTargetChildren = ElemsService.ungroupGroupChildren(
          targetGroupElem,
          targetGroupChildren,
        );

        const updatedTargetChildren = [layer, ...ungroupedTargetChildren];

        updatedLayersList = ElemsService.replaceGroupChildren(
          updatedLayersList,
          targetGroupElem.id,
          updatedTargetChildren,
        );

        const updatedLayer = updatedLayersList.find(
          (item) => item.id === layer?.id,
        );

        if (!updatedLayer) return;

        layer = updatedLayer;
      }
    }

    // переставляем элемент из начальной позиции в целевую
    updatedLayersList.splice(sourceIndex, 1);
    updatedLayersList.splice(destinationIndex, 0, layer);

    // слой группа
    if (layerType === LayerTypes.GROUP) {
      const sourceGroupElem = layer;
      // помещаем детей группы внутрь другой группы, а сам слой группы удаляем
      if (
        prevLayer &&
        (prevLayer.groupId || prevLayer.elemType === ElemTypes.GROUP) &&
        nextLayer?.groupId
      ) {
        const targetGroupElem = initialLayers.find(
          (item) => item.id === nextLayer?.groupId,
        );

        if (!targetGroupElem) return;

        const { sourceGroupChildren, targetGroupChildren } =
          this._getGroupElemsChildren(
            updatedLayersList,
            sourceGroupElem.id,
            targetGroupElem.id,
          );

        const sourceUngroupedChildren = ElemsService.ungroupGroupChildren(
          sourceGroupElem,
          sourceGroupChildren,
        );

        const targetUngroupedChildren = ElemsService.ungroupGroupChildren(
          targetGroupElem,
          targetGroupChildren,
        );

        const updatedTargetChildren = [
          ...sourceUngroupedChildren,
          ...targetUngroupedChildren,
        ];

        updatedLayersList = ElemsService.replaceGroupChildren(
          updatedLayersList,
          targetGroupElem.id,
          updatedTargetChildren,
        );

        layersToRemove.push(sourceGroupElem);
      }
    }

    updatedLayersList = this._removeLayersIfNeeded(
      layersToRemove,
      updatedLayersList,
    );

    const updatedElems = this._transformLayersToElems(updatedLayersList);

    dispatch(updateElems(updatedElems));
  };

  moveSelectedElemsDown(
    initialElems: IElement[],
    elemsIds: string[],
  ): IElement[] {
    const updatedElems: IElement[] = [...initialElems];

    // sort for the correct sequence of actions when shifting adjacent elements
    const sortedElemsIds = updatedElems
      .filter((elem) => elemsIds.includes(elem.id))
      .map((item) => item.id);

    sortedElemsIds.forEach((elemId) => {
      const elem = updatedElems.find((elem) => elem.id === elemId);

      if (!elem) return;

      const elemIndex = updatedElems.indexOf(elem);

      if (elemIndex === 0) return;

      const prevElem = updatedElems[elemIndex - 1];

      if (
        elemsIds.includes(prevElem.id) &&
        updatedElems.indexOf(prevElem) === 0
      ) {
        return;
      }

      updatedElems[elemIndex - 1] = elem;
      updatedElems[elemIndex] = prevElem;
    });

    return updatedElems;
  }

  moveSelectedElemsUp(
    initialElems: IElement[],
    elemsIds: string[],
  ): IElement[] {
    const updatedElems: IElement[] = [...initialElems];

    // sort for the correct sequence of actions when shifting adjacent elements
    const sortedElemsIds = updatedElems
      .filter((elem) => elemsIds.includes(elem.id))
      .map((item) => item.id)
      .reverse();

    sortedElemsIds.forEach((elemId) => {
      const elem = updatedElems.find((elem) => elem.id === elemId);

      if (!elem) return;

      const elemIndex = updatedElems.indexOf(elem);

      if (elemIndex === updatedElems.length - 1) return;

      const nextElem = updatedElems[elemIndex + 1];

      if (
        elemsIds.includes(nextElem.id) &&
        updatedElems.indexOf(nextElem) === updatedElems.length - 1
      )
        return;

      updatedElems[elemIndex + 1] = elem;
      updatedElems[elemIndex] = nextElem;
    });

    return updatedElems;
  }

  sendSelectedElemsToBack(
    initialElems: IElement[],
    elemsIds: string[],
  ): IElement[] {
    let updatedElems: IElement[] = [...initialElems];

    const selectedElems = updatedElems.filter((elem) =>
      elemsIds.includes(elem.id),
    );

    updatedElems = updatedElems.filter((elem) => !elemsIds.includes(elem.id));

    return [...selectedElems, ...updatedElems];
  }

  bringSelectedElemsToFront(
    initialElems: IElement[],
    elemsIds: string[],
  ): IElement[] {
    let updatedElems: IElement[] = [...initialElems];

    const selectedElems = updatedElems.filter((elem) =>
      elemsIds.includes(elem.id),
    );

    updatedElems = updatedElems.filter((elem) => !elemsIds.includes(elem.id));

    return [...updatedElems, ...selectedElems];
  }

  toggleNestedLayersVisibility(
    initialElems: IElement[],
    groupId: string,
  ): IElement[] {
    return initialElems.map((elem): IElement => {
      if (elem.id === groupId) {
        return { ...elem, showNestedLayers: !elem?.showNestedLayers };
      }
      return elem;
    });
  }

  toggleLayersVisibility(initialElems: IElement[], ids: string[]): IElement[] {
    return CollectionService.findByIdsAndUpdateWithHandler<IElement>(
      initialElems,
      ids,
      {
        isHidden: (value) => !value,
      },
    );
  }

  toggleLayersLocking(initialElems: IElement[], ids: string[]): IElement[] {
    return CollectionService.findByIdsAndUpdateWithHandler<IElement>(
      initialElems,
      ids,
      {
        isBlocked: (value) => !value,
      },
    );
  }

  removeFromSelectedByIds(
    selectedIds: string[],
    removedIds: string[],
  ): string[] {
    return selectedIds.filter((id) => !removedIds.includes(id));
  }

  deleteSelectedElems(
    elems: IElement[],
    ids: string[],
    needToGroupAgainCollection: IGroupAgainItem[],
  ): { filteredElems: IElement[]; updatedNeedToGroupAgain: IGroupAgainItem[] } {
    let needToRemoveIds = [...ids];

    const filteredElems = elems.filter((elem) => {
      if (needToRemoveIds.includes(elem.id)) {
        needToRemoveIds = needToRemoveIds.filter((id) => id !== elem.id);
        return false;
      }
      return true;
    });

    const updatedNeedToGroupAgain = needToGroupAgainCollection.filter(
      (item) => {
        const filteredItem = item.childrenIds.filter((id) => !ids.includes(id));

        if (filteredItem.length <= 1) {
          return false;
        }

        return true;
      },
    );

    return { filteredElems, updatedNeedToGroupAgain };
  }
}

export default new LayersService();
